import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import GenericFormDrawer from '../../../components/GenericFormDrawer';
import GenericSelect from '../../../components/ui/GenericSelect';
import TextInput from '../../../components/ui/TextInput';
import Button from '../../../components/ui/Button';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { approveScaleMapping, revokeScaleMapping } from '../../../services/performanceTrendsService';
import { formatDateTime } from '../../../utils/Utils';
import { fetchScaleMappings, fetchScaleOptions } from '../config';

// Analiz §14 E11 / M60 — "ölçeği farklı puanlar M60 üzerinden ve ölçek eşlemesi onayıyla gösterilir". Yalnız İK. Onay iki ölçüm
// ölçeğinin normalize puanla (0–100) dönemler arası karşılaştırılabileceğini kayda geçirir (denetimli); geri alınabilir.
// Kabuk GenericFormDrawer (modal yok); geri alma ConfirmDialog ile.
const Section = styled.h4`
  margin: 16px 0 8px;
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  margin-bottom: 6px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
`;

const Field = styled.div`
  margin-bottom: 12px;
`;

const Notice = styled.div`
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  border-left: 3px solid ${({ theme }) => theme.colors.auxiliar.warning};
  background: ${({ theme }) => theme.colors.bgColor};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const scaleName = (t, key, options) => {
  if (key === 'goal100') return t('performance.trends.goalScale');
  return (options || []).find((o) => o.scaleKey === key)?.name || key;
};

export default function ScaleMappingDrawer({ open, onClose, onChanged, suggested }) {
  const { t, i18n } = useTranslation();
  const [options, setOptions] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [form, setForm] = useState({ a: '', b: '', note: '' });
  const [revoke, setRevoke] = useState(null);

  const load = useCallback(async () => {
    const [opts, maps] = await Promise.all([fetchScaleOptions().catch(() => []), fetchScaleMappings().catch(() => [])]);
    setOptions(opts || []);
    setMappings(maps || []);
  }, []);

  useEffect(() => {
    if (!open) return;
    setForm({ a: suggested?.scaleKeyA || '', b: suggested?.scaleKeyB || '', note: '' });
    load();
  }, [open, load, suggested]);

  const choices = options.map((o) => ({ id: o.scaleKey, name: scaleName(t, o.scaleKey, options) }));

  const handleSave = useCallback(async () => {
    if (!form.a || !form.b || form.a === form.b) return false;
    try {
      const res = await approveScaleMapping({ scaleKeyA: form.a, scaleKeyB: form.b, note: form.note || null });
      if (!res?.data?.status) return false;
      onChanged?.();
      return true;
    } catch {
      return false;   // hata tostu interceptor'da
    }
  }, [form, onChanged]);

  const handleRevoke = useCallback(async () => {
    if (!revoke) return;
    try {
      const res = await revokeScaleMapping(revoke.id);
      if (res?.data?.status) {
        setRevoke(null);
        await load();
        onChanged?.();
      }
    } catch {
      setRevoke(null);
    }
  }, [revoke, load, onChanged]);

  return (
    <>
      <GenericFormDrawer
        open={open}
        onClose={onClose}
        title={t('performance.trends.scaleMapping.title')}
        schema={[]}
        submitLabel={t('performance.trends.scaleMapping.approve')}
        submitDisabled={!form.a || !form.b || form.a === form.b}
        onSave={handleSave}
      >
        <Notice>{t('performance.trends.scaleMapping.explain')}</Notice>
        <Field>
          <GenericSelect label={t('performance.trends.scaleMapping.scaleA')} options={choices} value={form.a}
            onChange={(v) => setForm((f) => ({ ...f, a: v || '' }))} idKey="id" labelKey="name" required />
        </Field>
        <Field>
          <GenericSelect label={t('performance.trends.scaleMapping.scaleB')} options={choices} value={form.b}
            onChange={(v) => setForm((f) => ({ ...f, b: v || '' }))} idKey="id" labelKey="name" required />
        </Field>
        <Field>
          <TextInput label={t('performance.trends.scaleMapping.note')} type="textarea" rows={3} value={form.note}
            onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
        </Field>
        <Section>{t('performance.trends.scaleMapping.existing')}</Section>
        {mappings.length === 0 ? <Muted data-state="empty">{t('performance.trends.scaleMapping.none')}</Muted> : null}
        {mappings.map((m) => (
          <Row key={m.id} data-mapping={m.id}>
            <div>
              <div>{`${m.scaleNameA || scaleName(t, m.scaleKeyA, options)} ↔ ${m.scaleNameB || scaleName(t, m.scaleKeyB, options)}`}</div>
              <Muted>{t('performance.trends.scaleMapping.approvedBy', { name: m.approvedBy || '-', time: formatDateTime(m.approvedAt, i18n.language) })}</Muted>
              {m.note ? <Muted>{m.note}</Muted> : null}
            </div>
            <Button variant="ghost" onClick={() => setRevoke(m)} data-action="revoke">{t('performance.trends.scaleMapping.revoke')}</Button>
          </Row>
        ))}
      </GenericFormDrawer>
      <ConfirmDialog
        isOpen={!!revoke}
        title={t('performance.trends.scaleMapping.revokeTitle')}
        message={t('performance.trends.scaleMapping.revokeMessage')}
        confirmText={t('performance.trends.scaleMapping.revoke')}
        cancelText={t('common.cancel')}
        onConfirm={handleRevoke}
        onCancel={() => setRevoke(null)}
      />
    </>
  );
}
